import type { ChangeEvent, DragEvent, RefObject } from "react";
import { useCallback, useRef } from "react";

const dragEventHasExternalFiles = (event: DragEvent<HTMLElement>) =>
  Array.from(event.dataTransfer.types).includes("Files");

interface UseFolderImportParams {
  readOnly: boolean;
  onFolderImport?: (
    files?: FileList,
    items?: DataTransferItemList,
  ) => Promise<boolean | void> | boolean | void;
  folderImportDisabled: boolean;
  onExternalDragOver: () => void;
  onExternalDragLeave: () => void;
  onFolderImportDrop: () => void;
}

interface UseFolderImportResult {
  folderImportInputRef: RefObject<HTMLInputElement>;
  handleTreeDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  handleOsFolderImportDragOverCapture: (event: DragEvent<HTMLDivElement>) => void;
  handleOsFolderImportDropCapture: (event: DragEvent<HTMLDivElement>) => void;
  handleFolderImportButtonClick: () => Promise<void>;
  handleFolderInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const useFolderImport = ({
  readOnly,
  onFolderImport,
  folderImportDisabled,
  onExternalDragOver,
  onExternalDragLeave,
  onFolderImportDrop,
}: UseFolderImportParams): UseFolderImportResult => {
  const folderImportInputRef = useRef<HTMLInputElement>(null);

  const handleTreeDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const related = event.relatedTarget as Node | null;
      if (related && (event.currentTarget as Node).contains(related)) {
        return;
      }

      if (dragEventHasExternalFiles(event)) {
        onExternalDragLeave();
      }
    },
    [onExternalDragLeave],
  );

  const handleOsFolderImportDragOverCapture = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (
        readOnly ||
        !onFolderImport ||
        folderImportDisabled ||
        !dragEventHasExternalFiles(event)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "copy";
      onExternalDragOver();
    },
    [folderImportDisabled, onExternalDragOver, onFolderImport, readOnly],
  );

  const handleOsFolderImportDropCapture = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (
        readOnly ||
        !onFolderImport ||
        folderImportDisabled ||
        event.dataTransfer.files.length === 0
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onFolderImportDrop();
      void onFolderImport(event.dataTransfer.files, event.dataTransfer.items);
    },
    [folderImportDisabled, onFolderImport, onFolderImportDrop, readOnly],
  );

  const handleFolderImportButtonClick = useCallback(async () => {
    if (!onFolderImport) {
      return;
    }

    const handled = await onFolderImport();
    if (handled === false) {
      folderImportInputRef.current?.click();
    }
  }, [onFolderImport]);

  const handleFolderInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const picked = event.target.files;
      if (picked && picked.length > 0) {
        void onFolderImport?.(picked);
      }

      event.target.value = "";
    },
    [onFolderImport],
  );

  return {
    folderImportInputRef,
    handleTreeDragLeave,
    handleOsFolderImportDragOverCapture,
    handleOsFolderImportDropCapture,
    handleFolderImportButtonClick,
    handleFolderInputChange,
  };
};
